/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-awesome-styled-grid";
import {
  Box,
  Button,
  FlexContainer,
  Spacer,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  useCustomerCreatedAtQuery,
  useGetMonthlyReportLazyQuery,
} from "~/generated/graphql";
import { BreadcrumbsBox, GlobalSpinner } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import { isEmpty } from "~/tools";
import { BillingPeriodSelector } from "./BillingPeriodSelector";
import { initialIncluded, newestReportDate } from "./constants";
import { IncludedTypeSelector } from "./IncludedTypeSelector";
import { BillingPeriod, Included } from "./types";
import { getMonthOptions, getRequestData } from "./utils";

export function Archive() {
  const [year, setYear] = useState<BillingPeriod["year"]>("");
  const [month, setMonth] = useState<BillingPeriod["month"]>("");
  const [startDate, setStartDate] = useState("");
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [included, setIncluded] = useState<Included>(initialIncluded);

  const [getReport, { loading }] = useGetMonthlyReportLazyQuery({
    // Cache is disabled to enable download the same report multiple times
    fetchPolicy: "no-cache",
    // Required for `onCompleted` to be run every time `getReport` is executed
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      showError(error.message ?? ERROR_TEXT.default);
    },
    onCompleted: (data) => {
      if (typeof window !== "undefined" && data.monthlyReport != null) {
        window.location.href = data.monthlyReport?.fileUrl || "";
        showSuccess(SUCCESS_TEXT.download);
      }
    },
  });

  useCustomerCreatedAtQuery({
    onCompleted: (customerData) => {
      const customerDate = customerData?.organization?.createdAt?.slice(0, 4);

      setStartDate(
        dayjs(customerData?.organization?.createdAt).format("YYYY-MM")
      );

      if (customerDate != null) {
        setYear(customerDate);
        setYearOptions(
          getNumberStringsBetween(
            dayjs(customerDate).year(),
            newestReportDate.year()
          )
        );
      }
    },
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    await getReport({
      variables: getRequestData({
        year,
        month,
        ...included,
      }),
    });
  };

  const monthOptions = getMonthOptions(startDate, year);

  return (
    <>
      <GlobalSpinner loading={loading} />
      <Row>
        <Col>
          <BreadcrumbsBox title="Archive" />
          <Box spacing="spacing04">
            <form
              id="get-report"
              css={{
                width: "100%",
                maxWidth: "650px",
                margin: "0 auto",
              }}
              onSubmit={handleSubmit}
            >
              <Box boxStyle="lightGrey">
                <Row>
                  <Col xs={0.5}>
                    <FlexContainer justifyContent="center" alignItems="center">
                      <SVGIcon
                        css={{ marginTop: ".3rem" }}
                        size="lg"
                        name="report"
                      />
                    </FlexContainer>
                  </Col>
                  <Col xs={3.5}>
                    <FlexContainer alignItems="flex-start" direction="column">
                      <Text tag="div" size="md" weight="medium">
                        Fill in the form below to prepare CSV report.
                      </Text>
                      <Text
                        tag="div"
                        size="sm"
                        weight="regular"
                        color={theme.color.text.text02}
                      >
                        Report will be delivered to your mailbox
                      </Text>
                    </FlexContainer>
                    <FlexContainer alignItems="flex-start" direction="column">
                      <BillingPeriodSelector
                        yearValue={year}
                        yearOptions={yearOptions}
                        monthValue={month}
                        monthOptions={monthOptions}
                        onMonthChange={setMonth}
                        onYearChange={setYear}
                      />
                    </FlexContainer>
                  </Col>
                </Row>
              </Box>
              <Spacer height={theme.spacing.spacing04} />
              <IncludedTypeSelector
                included={included}
                setIncluded={setIncluded}
              />
              <Spacer height={theme.spacing.spacing04} />
              <Box boxStyle="lightGrey">
                <Text
                  tag="div"
                  size="sm"
                  weight="regular"
                  color={theme.color.text.text02}
                >
                  <SVGIcon
                    name="info"
                    color="notification"
                    size="sm"
                    css={{
                      position: "relative",
                      top: "3px",
                      marginRight: "5px",
                    }}
                  />
                  The report will include a list of all applications,
                  environments, and resources attached to those environments. It
                  is enriched by information about all Business Contexts and
                  Organizational Units for the selected Billing Period
                </Text>
              </Box>
              <Spacer height={theme.spacing.spacing04} />
              <FlexContainer alignItems="center" justifyContent="center">
                <Button disabled={isEmpty(month)} type="submit">
                  Get Report
                </Button>
              </FlexContainer>
            </form>
          </Box>
        </Col>
      </Row>
    </>
  );
}

function getNumberStringsBetween(start: number, end: number): string[] {
  const years: string[] = [];

  for (let index = start; index <= end; index++) {
    years.push(index.toString());
  }

  return years;
}
